/**
 * API Services for New Features
 */

import { 
  ListShare, 
  ListSnapshot, 
  SmartSuggestions, 
  Recipe, 
  RecipeHistory, 
  PromoAlert, 
  UserPreference,
  AIGeneratedItem
} from '../types/features';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// ==================== COLLABORATIVE ====================

export const collaborativeService = {
  shareList: async (listId: number, userId: string, permission: string = 'view'): Promise<ListShare> => {
    const res = await fetch(`${API_URL}/api/collaborative/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ listId, userId, permission })
    });
    const data = await res.json();
    return data.share;
  },

  getSharedLists: async (userId: string): Promise<ListShare[]> => {
    const res = await fetch(`${API_URL}/api/collaborative/shared/${userId}`);
    const data = await res.json();
    return data.lists;
  },

  createSnapshot: async (listId: number): Promise<ListSnapshot> => {
    const res = await fetch(`${API_URL}/api/collaborative/snapshot/${listId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await res.json();
    return data.snapshot;
  },

  getHistory: async (listId: number, limit: number = 10): Promise<ListSnapshot[]> => {
    const res = await fetch(`${API_URL}/api/collaborative/history/${listId}?limit=${limit}`);
    const data = await res.json();
    return data.snapshots;
  },

  removeShare: async (shareId: number): Promise<void> => {
    await fetch(`${API_URL}/api/collaborative/share/${shareId}`, {
      method: 'DELETE'
    });
  }
};

// ==================== AI CONTEXT ====================

export const aiContextService = {
  learn: async (userId: string, items: any[]): Promise<void> => {
    await fetch(`${API_URL}/api/ai-context/learn`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, items })
    });
  },

  getSuggestions: async (userId: string, season?: string): Promise<SmartSuggestions> => {
    const url = new URL(`${API_URL}/api/ai-context/suggestions/${userId}`);
    if (season) url.searchParams.set('season', season);
    
    const res = await fetch(url.toString());
    const data = await res.json();
    return data.suggestions;
  },

  generateSmart: async (userId: string, prompt: string, useContext: boolean = true): Promise<AIGeneratedItem[]> => {
    const res = await fetch(`${API_URL}/api/ai-context/generate-smart`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, prompt, useContext })
    });
    const data = await res.json();
    return data.items;
  },

  getStats: async (userId: string): Promise<any> => {
    const res = await fetch(`${API_URL}/api/ai-context/stats/${userId}`);
    const data = await res.json();
    return data.stats;
  }
};

// ==================== RECIPES ====================

export const recipesService = {
  generate: async (ingredients: string[], preferences?: string, servings?: number): Promise<Recipe> => {
    const res = await fetch(`${API_URL}/api/recipes/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ingredients, preferences, servings })
    });
    const data = await res.json();
    return data.recipe;
  },

  save: async (recipe: Recipe): Promise<Recipe> => {
    const res = await fetch(`${API_URL}/api/recipes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(recipe)
    });
    const data = await res.json();
    return data.recipe;
  },

  list: async (filters?: { cuisine?: string; difficulty?: string; tags?: string[] }): Promise<Recipe[]> => {
    const url = new URL(`${API_URL}/api/recipes`);
    if (filters?.cuisine) url.searchParams.set('cuisine', filters.cuisine);
    if (filters?.difficulty) url.searchParams.set('difficulty', filters.difficulty);
    if (filters?.tags) url.searchParams.set('tags', filters.tags.join(','));
    
    const res = await fetch(url.toString());
    const data = await res.json();
    return data.recipes;
  },

  get: async (id: number): Promise<Recipe> => {
    const res = await fetch(`${API_URL}/api/recipes/${id}`);
    const data = await res.json();
    return data.recipe;
  },

  toList: async (id: number, servings?: number): Promise<AIGeneratedItem[]> => {
    const res = await fetch(`${API_URL}/api/recipes/${id}/to-list`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ servings })
    });
    const data = await res.json();
    return data.items;
  },

  markCooked: async (id: number, userId: string, rating?: number, notes?: string): Promise<RecipeHistory> => {
    const res = await fetch(`${API_URL}/api/recipes/${id}/history`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, rating, notes })
    });
    const data = await res.json();
    return data.history;
  },

  getHistory: async (userId: string): Promise<RecipeHistory[]> => {
    const res = await fetch(`${API_URL}/api/recipes/history/${userId}`);
    const data = await res.json();
    return data.history;
  }
};

// ==================== ALERTS ====================

export const alertsService = {
  scan: async (userId: string): Promise<any> => {
    const res = await fetch(`${API_URL}/api/alerts/scan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId })
    });
    return await res.json();
  },

  getAlerts: async (userId: string, unreadOnly: boolean = false): Promise<PromoAlert[]> => {
    const url = new URL(`${API_URL}/api/alerts/${userId}`);
    if (unreadOnly) url.searchParams.set('unreadOnly', 'true');
    
    const res = await fetch(url.toString());
    const data = await res.json();
    return data.alerts;
  },

  markAsRead: async (alertId: number): Promise<void> => {
    await fetch(`${API_URL}/api/alerts/${alertId}/read`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' }
    });
  },

  updatePreferences: async (userId: string, preferences: Partial<UserPreference>): Promise<UserPreference> => {
    const res = await fetch(`${API_URL}/api/alerts/preferences`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, ...preferences })
    });
    const data = await res.json();
    return data.preferences;
  },

  getPreferences: async (userId: string): Promise<UserPreference> => {
    const res = await fetch(`${API_URL}/api/alerts/preferences/${userId}`);
    const data = await res.json();
    return data.preferences;
  }
};
